import { z } from "zod";

// Entity association schema for appointments
export const entityAssociationSchema = z.object({
  entityType: z.string().min(1, { message: "Entity type is required" }),
  entityId: z.string().uuid({ message: "Entity ID must be a valid UUID" }),
  notes: z.string().optional(),
  entityName: z.string().optional(),
  metadata: z.record(z.string(), z.any()).optional(),
});

// Base appointment schema matching the backend API
export const appointmentSchema = z.object({
  id: z.string(),
  userId: z.string(),
  title: z.string().min(1, { message: "Title is required" }),
  // Legacy fields for backward compatibility
  time: z.string().regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, {
    message: "Time must be in HH:MM format"
  }).optional(),
  date: z.string().optional(),
  // Length of the appointment in minutes. The backend column already exists
  // (default 30, populated on every row) but the form never exposed it, so
  // every booking silently defaulted and overlap checks had nothing to work
  // with.
  duration: z.number().int().positive().max(480).optional(),
  notes: z.string().optional(),
  // New fields matching backend model
  description: z.string().optional(),
  startTime: z.date().or(z.string()).optional(),
  endTime: z.date().or(z.string()).optional(),
  meetingLink: z.string().url().optional().or(z.literal("")),
  leadId: z.string().uuid().optional(),
  agencyId: z.string().uuid().optional(),
  priorityStatus: z.enum(['low', 'medium', 'high', 'urgent']).optional().default('medium'),
  associations: z.array(z.object({
    id: z.string(),
    appointmentId: z.string(),
    entityType: z.string(),
    entityName: z.string().optional(),
    entityId: z.string(),
    notes: z.string().optional(),
    metadata: z.object({
      appStat: z.enum(['scheduled', 'rescheduled', 'pending', 'confirmed', 'completed', 'cancelled', 'no_show', 'overdue']).optional(),
    }).optional(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
    deletedAt: z.string().nullable().optional(),
  })).optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  deletedAt: z.string().nullable().optional(),
  // Calendar Sync Fields (for OAuth calendar integration)
  googleCalendarEventId: z.string().optional(),
  googleCalendarSyncStatus: z.enum(['synced', 'pending', 'failed', 'none']).optional(),
  googleCalendarLastSyncAt: z.string().optional(),
  googleCalendarSyncError: z.string().optional(),
  microsoftCalendarEventId: z.string().optional(),
  microsoftCalendarSyncStatus: z.enum(['synced', 'pending', 'failed', 'none']).optional(),
  microsoftCalendarLastSyncAt: z.string().optional(),
  microsoftCalendarSyncError: z.string().optional(),
  syncMetadata: z.object({
    source: z.enum(['app', 'google', 'microsoft']).optional(),
    lastModifiedSource: z.enum(['app', 'google', 'microsoft']).optional(),
    syncVersion: z.number().optional(),
    syncHash: z.string().optional(),
    conflictResolution: z.enum(['last_write_wins', 'app_priority', 'calendar_priority', 'manual']).optional(),
  }).optional(),
});

// Schema for creating a new appointment
export const createAppointmentSchema = z.object({
  title: z.string().min(1, { message: "Title is required" }),
  // Legacy fields - required for UI but will be converted to new fields
  time: z.string().regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, {
    message: "Time must be in HH:MM format"
  }),
  apptDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, {
    message: "Date must be in YYYY-MM-DD format"
  }),
  duration: z.number().int().positive().max(480).optional(),
  apptNotes: z.string().optional(),
  // New fields - populated automatically from legacy fields
  description: z.string().optional(),
  startTime: z.date().or(z.string()).optional(),
  endTime: z.date().or(z.string()).optional(),
  meetingLink: z.string().url().optional().or(z.literal("")),
  leadId: z.string().uuid().optional(),
  agencyId: z.string().uuid().optional(),
  priorityStatus: z.enum(['low', 'medium', 'high', 'urgent']),
  // Existing fields
  status: z.enum(['scheduled', 'rescheduled', 'pending', 'confirmed', 'completed', 'cancelled', 'no_show', 'overdue']).optional(),
  dstOffset: z.number().optional(),
  leadrawOffset: z.number().optional(),
  appStat: z.enum(['pending', 'confirmed', 'completed', 'cancelled', 'no_show']).optional(),
  sendEmailConf: z.boolean().optional(),
  associations: z.array(entityAssociationSchema).optional(),
});

// Schema for updating an appointment

// Schema for appointment ID
export const appointmentIdSchema = z.object({
  appointmentId: z.string().min(1, { message: "Appointment ID is required" }),
});

export const updateAppointmentSchema = z.object({
  title: z.string().min(1).optional(),
  // Legacy fields for backward compatibility
  time: z.string().regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, {
    message: "Time must be in HH:MM format"
  }).optional(),
  apptDate: z.string().min(1).optional(),
  duration: z.number().int().positive().max(480).optional(),
  apptNotes: z.string().optional(),
  // New fields matching backend model
  description: z.string().optional(),
  startTime: z.date().or(z.string()).optional(),
  endTime: z.date().or(z.string()).optional(),
  meetingLink: z.string().url().optional().or(z.literal("")),
  leadId: z.string().uuid().optional(),
  agencyId: z.string().uuid().optional(),
  priorityStatus: z.enum(['low', 'medium', 'high', 'urgent']).optional(),
  // Existing fields
  status: z.enum(['scheduled', 'rescheduled', 'pending', 'confirmed', 'completed', 'cancelled', 'no_show', 'overdue']).optional(),
  appStat: z.enum(['pending', 'confirmed', 'completed', 'cancelled', 'no_show']).optional(),
  associations: z
    .array(
      z.object({
        entityType: z.string(),
        entityId: z.string().uuid(),
        notes: z.string().optional(),
        metadata: z.record(z.string(), z.any()).optional(),
        appStat: z.enum(['scheduled', 'rescheduled', 'pending', 'confirmed', 'completed', 'cancelled', 'no_show', 'overdue']).optional(),
      })
    )
    .optional(),
});

// Type exports
export type Appointment = z.infer<typeof appointmentSchema>;
export type CreateAppointment = z.infer<typeof createAppointmentSchema>;
export type UpdateAppointment = z.infer<typeof updateAppointmentSchema>;
export type AppointmentId = z.infer<typeof appointmentIdSchema>;
export type EntityAssociation = z.infer<typeof entityAssociationSchema>;
